/**
 * Functions to smoothly scroll browser window
 *
 * @TODO: Use native browser functionality when it becomes available in iOS
 * Safari: https://caniuse.com/scrollintoview
 *
 * @author mcg, bgr
 */

import { pause, resume } from "../../shared/event/visibility-observer";

const DURATION = 250;
const TICK = 10;
const STEPS = DURATION / TICK;

/**
 * Scroll by given delta (negative values scroll upwards)
 *
 * @param {int} delta
 * @return {Promise<void>} resolved after the scrolling finished
 */
export function scrollBy(delta) {
	if (delta == 0) {
		return Promise.resolve();
	}

	return new Promise(resolve => {
		const startValue = window.pageYOffset;
		let counter = 0;
		pause();
		let interval = setInterval(() => {
			counter++;
			window.scrollTo(
				0,
				startValue + delta * (Math.log(counter) / Math.log(STEPS)),
			);
			if (counter >= STEPS) {
				clearInterval(interval);
				resume();
				resolve();
			}
		}, TICK);
	});
}

/**
 * Scroll given node to top of browser window
 *
 * @param {Element} node
 * @param {Number} padding
 * @return {Promise<void>} resolved after the scrolling finished
 */
export function scrollToTop(node, padding = 0) {
	const top = node.getBoundingClientRect().top - padding;
	return scrollBy(top);
}

/**
 * Minimal scrolling to have node element completely visible,
 * if not possible then scroll node to top
 *
 * @param {Element} node
 * @param {Number} targetHeight (optional) set if height is not yet set correct (i.e. for transitions)
 * @return {Promise<void>} resolved after the scrolling finished
 */
export function scrollToCompletelyVisible(node, targetHeight = undefined) {
	const windowHeight = document.documentElement.clientHeight;
	const { top, height } = node.getBoundingClientRect();
	targetHeight = targetHeight || height;
	// If node is smaller than browser window and
	// its top corner position is not over top window corner,
	// check for minimal needed delta
	let delta = top;
	if (delta > 0 && targetHeight < windowHeight) {
		delta = delta + targetHeight - windowHeight;

		// No scrolling if node is already completely visible
		if (delta < 0) {
			return;
		}
	}
	return scrollBy(delta);
}
